import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Divider,
  Paper,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DoneRounded from '@mui/icons-material/DoneRounded';
import * as actions from "../../../store/actions";
import { i18n } from '../../../translate/i18n';
import confirm from '../../../UI/Confirm/confirm';

export type IUpdateNotificationWebhookBody = {
  showActionsNotifications: boolean,
  showServicesNotifications: boolean,
  active: boolean,
  apiKey: string,
  actionsUrl: string,
  servicesUrl: string,
}

const FormParamNotificationWebhook: React.FC = () => {
  const dispatch = useDispatch();
  const tenantId = useSelector((state: any) => state.auth.selectedAccess.tenant._id);

  const getNotificationWebhook = () => dispatch(actions.tenantGetNotificationWebhook(tenantId));
  const updateNotificationWebhook = (body: IUpdateNotificationWebhookBody) => dispatch(actions.tenantUpdateNotificationWebhook(tenantId, body));

  const notificationWebhook = useSelector((state: any) => state.tenants.notificationWebhook);
  const loading = useSelector((state: any) => state.tenants.loadingNotificationWebhook);

  const [actionsUrl, setActionsUrl] = useState<string>(notificationWebhook.actionsUrl);
  const [servicesUrl, setServicesUrl] = useState<string>(notificationWebhook.servicesUrl);

  const [showActionsNotifications, setShowActionsNotifications] = useState<boolean>(notificationWebhook.showActionsNotifications);
  const [showServicesNotifications, setShowServicesNotifications] = useState<boolean>(notificationWebhook.showServicesNotifications);
  const [active, setActive] = useState<boolean>(notificationWebhook.active);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [actionsUrlError, setActionsUrlError] = useState<boolean>(false);
  const [servicesUrlError, setServicesUrlError] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>(notificationWebhook.apiKey);

  useEffect(() => {
    getNotificationWebhook();
  }, [])

  useEffect(() => {
    setActionsUrl(notificationWebhook.actionsUrl);
    setServicesUrl(notificationWebhook.servicesUrl);
    setShowActionsNotifications(notificationWebhook.showActionsNotifications);
    setShowServicesNotifications(notificationWebhook.showServicesNotifications);
    setActive(notificationWebhook.active);
    setApiKey(notificationWebhook.apiKey);
  }, [notificationWebhook.actionsUrl, notificationWebhook.servicesUrl])

  const isUrlValid = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (err) {
      return false;
    }
  }

  const handleDelete = () => {
    confirm(i18n.t('formParamNotificationWebhook.deleteWebhookMessage'),
      {
        title: i18n.t('formParamNotificationWebhook.deleteWebhook'),
        titleColor: 'red',
        disableBackdrop: true
      }).then(
        () => { },
        () => {
          handleDeleteNotificationWebhook()
        },
      )
  }
  
  const handleDeleteNotificationWebhook = () => {
    updateNotificationWebhook({active: false, showActionsNotifications: false, showServicesNotifications: false, actionsUrl: '', servicesUrl: '', apiKey: ''});
  }

  const handleUpdateNotificationWebhook = () => {
    const isActionsUrlValid = actionsUrl !== '' && isUrlValid(actionsUrl);
    const isServicesUrlValid = servicesUrl !== '' && isUrlValid(servicesUrl);

    let error = false;
    if (showActionsNotifications && !isActionsUrlValid) {
      error = true;
      setActionsUrlError(true);
    }

    if (showServicesNotifications && !isServicesUrlValid) {
      error = true;
      setServicesUrlError(true);
    }

    if (!error) {
      setEditMode(false);
      return updateNotificationWebhook({active: true, showActionsNotifications, showServicesNotifications, actionsUrl, servicesUrl, apiKey});
    }
  }

  const handleCancelEdit = () => {
    setEditMode(false);
    setActionsUrl(notificationWebhook.actionsUrl);
    setServicesUrl(notificationWebhook.servicesUrl);
    setShowActionsNotifications(notificationWebhook.showActionsNotifications);
    setShowServicesNotifications(notificationWebhook.showServicesNotifications);
    setActive(notificationWebhook.active);
    setApiKey(notificationWebhook.apiKey);
  }
  
  return (
    <Container maxWidth='md'>
      <Paper sx={{...styles.paper, opacity: loading ? 0.3: 1}}>
        {loading && <CircularProgress
          variant="indeterminate"
          disableShrink
          size={30}
          thickness={4}
          sx={{position: 'absolute', left: '50%', top: '50%', zIndex: 100}}
        />}
        <Box sx={styles.titleContainer}>
          <Box sx={{display: 'flex', gap: 2}}>
            <Typography variant='h6'>Webhook</Typography>
            {active && <Chip label={i18n.t('formParamNotificationWebhook.active') as string} color='primary'/>}
          </Box>
          {active && <Box sx={styles.iconButtonsContainer}>
            {editMode ?
              <>
                <IconButton onClick={handleCancelEdit} size="large"><CloseRounded color='error' /></IconButton>
                <IconButton onClick={handleUpdateNotificationWebhook} size="large"><DoneRounded color='primary' /></IconButton>
              </> 
              :
              <>
                <IconButton onClick={() => setEditMode(true)} size="large"><EditRounded /></IconButton>
                <IconButton onClick={handleDelete} size="large"><DeleteRounded /></IconButton>
              </>
            }
          </Box>}
        </Box>
        <Typography variant='subtitle2'>{i18n.t('formParamNotificationWebhook.subtitle') as string}</Typography>
        <Divider />
        <Box>
          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Switch
                  disabled={!editMode && active}
                  name='showActionsNotifications'
                  checked={showActionsNotifications}
                  onChange={event => setShowActionsNotifications(event.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t('formParamNotificationWebhook.showActionsLabel') as string}
            />
            <FormControl sx={styles.firstSwitch} variant="standard" fullWidth>
              <TextField
                label='URL Ações'
                variant='outlined'
                name='webhookUrlActions'
                onChange={ (event) => { setActionsUrlError(false); setActionsUrl(event.target.value);} }
                value={actionsUrl}
                margin='normal'
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={(!editMode && active) || (!showActionsNotifications)}
                sx={{marginBottom: '5px'}}
                placeholder='https://geequip.sistemasgap.com.br/actions'
              />
              {actionsUrlError && <Typography sx={{color: 'red', fontSize: '12px', marginBottom: '10px'}}>{i18n.t('formParamNotificationWebhook.invalidUrl') as string}</Typography>}
            </FormControl>
            <FormHelperText sx={styles.helperText}>{i18n.t('formParamNotificationWebhook.actionsHelperText') as string}</FormHelperText>
            <FormHelperText sx={styles.helperText}>{i18n.t('formParamNotificationWebhook.actionsHelperText2') as string}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Switch
                  disabled={!editMode && active}
                  name='showServicesNotifications'
                  checked={showServicesNotifications}
                  onChange={event => setShowServicesNotifications(event.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t('formParamNotificationWebhook.showServicesLabel') as string}
            />
            <FormControl sx={styles.firstSwitch} variant="standard" fullWidth>
              <TextField
                label='URL Serviços'
                variant='outlined'
                name='webhookUrlServices'
                onChange={ (event) => { setServicesUrlError(false); setServicesUrl(event.target.value);} }
                value={servicesUrl}
                margin='normal'
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={(!editMode && active) || (!showServicesNotifications)}
                sx={{marginBottom: '5px'}}
                placeholder='https://geequip.sistemasgap.com.br/services'
              />
              {servicesUrlError && <Typography sx={{color: 'red', fontSize: '12px', marginBottom: '10px'}}>{i18n.t('formParamNotificationWebhook.invalidUrl') as string}</Typography>}
            </FormControl>
            <FormHelperText sx={styles.helperText}>{i18n.t('formParamNotificationWebhook.servicesHelperText') as string}</FormHelperText>
            <FormHelperText sx={styles.helperText}>{i18n.t('formParamNotificationWebhook.servicesHelperText2') as string}</FormHelperText>
          </FormControl>
          <FormControl variant="standard" fullWidth>
            <TextField
              label='Api Key'
              variant='outlined'
              name='webhookApiKey'
              onChange={ (event) => { setServicesUrlError(false); setActionsUrlError(false); setApiKey(event.target.value);} }
              value={(editMode || !active) ? apiKey : apiKey.replace(/.(?=.{4})/g, '*')}
              margin='normal'
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!editMode && active}
              sx={{marginBottom: '5px'}}
              placeholder='3838630f-83ca-4243-9053-0508b6cfea6e'
            />
            <FormHelperText sx={styles.helperText}>{i18n.t('formParamNotificationWebhook.apiKeyHelperText') as string}</FormHelperText>
          </FormControl>
        </Box>
        {(!active && (showActionsNotifications || showServicesNotifications)) && <Box sx={styles.buttonsContainer}>
          <Button
            color='primary'
            variant='contained'
            sx={styles.button}
            onClick={handleUpdateNotificationWebhook}
          >
            <Typography>{i18n.t('formParamNotificationWebhook.createWebhook') as string}</Typography>
          </Button>
        </Box>}
      </Paper>
    </Container>
  )
};

export default FormParamNotificationWebhook;

const styles = {
  paper: {
    padding: '10px 20px',
    position: 'relative'
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  iconButtonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
  },
  firstSwitch: {
  },
  switchContainer: {
    display: 'flex'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
    marginBottom: '12px',
  },
  button: {
    display: 'flex',
  },
  helperText: {marginLeft: 0 },
}
